import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { HeroSlide } from "@/types/content";
import { films } from "@/mocks/films";

interface HeroCarouselProps {
  slides: HeroSlide[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % slides.length);
    }, 7000);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  const goTo = (next: number) => {
    setIndex((next + slides.length) % slides.length);
  };

  const slide = slides[index];
  const film = films.find((item) => item.id === slide.filmId);

  return (
    <section className="relative h-[440px] w-full overflow-hidden rounded-xl bg-primary-950 md:h-[520px]">
      {slides.map((item, slideIndex) => (
        <div
          key={item.id}
          className={
            slideIndex === index
              ? "absolute inset-0 opacity-100 transition-opacity duration-700"
              : "absolute inset-0 opacity-0 transition-opacity duration-700"
          }
          aria-hidden={slideIndex !== index}
        >
          <img
            src={item.image}
            alt={`${item.headline} 焦点影片配图`}
            title={`${item.headline} - 三级片电影详情介绍`}
            className="h-full w-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/55 to-primary-950/25" />
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/70 via-primary-950/20 to-transparent" />

      <div className="absolute inset-0 flex w-full flex-col justify-end p-5 md:p-9">
        <div key={slide.id} className="fade-in w-full max-w-xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500/95 px-3 py-1 text-[11px] font-medium text-primary-950">
            <i className="ri-fire-line" />
            {slide.kicker}
          </span>
          <h2 className="mt-3 font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
            {slide.headline}
          </h2>
          <p className="mt-3 line-clamp-3 max-w-lg text-sm leading-relaxed text-background-200/90 md:text-[15px]">
            {slide.description}
          </p>
          {film && (
            <p className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-background-300">
              <span>{film.year}</span>
              <span className="text-background-500">/</span>
              <span>{film.region}</span>
              <span className="text-background-500">/</span>
              <span>{film.duration} 分钟</span>
              <span className="flex items-center gap-1 text-secondary-300">
                <i className="ri-star-fill text-[10px]" />
                {film.rating.toFixed(1)}
              </span>
            </p>
          )}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${slide.filmId}`}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-5 py-2.5 text-sm font-medium text-primary-950 transition-colors hover:bg-secondary-400"
            >
              <i className="ri-file-text-line" />
              查看作品资料
            </Link>
            <Link
              to={film ? `/category/${film.categoryKey}` : "/category/drama"}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-200/40 px-5 py-2.5 text-sm text-background-100 transition-colors hover:bg-background-50/10"
            >
              浏览同类影片
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-5 right-5 flex items-center gap-3 md:bottom-8 md:right-8">
        <div className="flex items-center gap-2">
          {slides.map((item, slideIndex) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setIndex(slideIndex)}
              aria-label={`切换到第 ${slideIndex + 1} 张焦点图`}
              className={
                slideIndex === index
                  ? "h-1.5 w-7 cursor-pointer rounded-full bg-secondary-400"
                  : "h-1.5 w-3 cursor-pointer rounded-full bg-background-200/40 transition-all hover:bg-background-200/70"
              }
            />
          ))}
        </div>
        <div className="hidden items-center gap-2 md:flex">
          <button
            type="button"
            onClick={() => goTo(index - 1)}
            aria-label="上一张"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-200/30 text-background-100 transition-colors hover:bg-background-50/15"
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>
          <button
            type="button"
            onClick={() => goTo(index + 1)}
            aria-label="下一张"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-200/30 text-background-100 transition-colors hover:bg-background-50/15"
          >
            <i className="ri-arrow-right-s-line text-lg" />
          </button>
        </div>
      </div>
    </section>
  );
}